import { GridApiCommon, GridEventListener, GridInitialState, GridRowId } from "@mui/x-data-grid";
import { GridCellSelectionModel } from "@mui/x-data-grid-premium";

import { LocaleStorageKeys } from "@/constants";
import { HotelClinicListItem, HotelClinicTaskResolved } from "@/pages/HotelPlanning/clinic-arrival/types";
import { IHotelPlanningDepartureResponseItem } from "@/schemas/hotelPlanningDepartureSchema";
import { ClinicTasksEditableFields } from "@/stores/use-clinic-arrival-tasks-grid";
import { ClinicUnassignedEditableFields } from "@/stores/use-clinic-arrival-unassigned-passengers-table";
import { HotelEditableFields } from "@/stores/use-hotel-planning-departure-table";
import { PlanEditableFields } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

export const getGridOptions = (
    storageKey: LocaleStorageKeys,
    newColumnNames?: string[]
): GridInitialState | undefined => {
    const state = localStorage.getItem(storageKey);
    if (!state) return;
    const settings = JSON.parse(state) as { state: GridInitialState };
    if (!settings || !settings.state) return;
    if (newColumnNames) {
        const orderedFields = settings.state.columns?.orderedFields;
        const visibleFields = settings.state.columns?.columnVisibilityModel;

        if (!visibleFields || !orderedFields) return settings.state;
        for (const name of newColumnNames) {
            if ("name" in visibleFields && orderedFields?.includes(name)) continue;
            if (orderedFields && !orderedFields.includes(name)) {
                orderedFields.splice(orderedFields.length - 2, 0, name);
            }
            if (visibleFields && !visibleFields[name]) {
                visibleFields[name] = false;
            }
        }
    }

    return settings.state;
};

export const onGridCellKeyDownCallback = <
    T extends
        | IPlanPassenger<"api">
        | IHotelPlanningDepartureResponseItem
        | HotelClinicListItem
        | HotelClinicTaskResolved,
    E extends PlanEditableFields | HotelEditableFields | ClinicUnassignedEditableFields | ClinicTasksEditableFields,
>(
    { cellMode, hasFocus, field, isEditable }: GridEventListener<"cellKeyDown">["arguments"][0],
    event: GridEventListener<"cellKeyDown">["arguments"][1],
    { api }: GridEventListener<"cellKeyDown">["arguments"][2],
    selectionModel: GridCellSelectionModel,
    processRowUpdate: (newRow: T, oldRow: T) => T,
    handleRowModesModelCancel: (id: GridRowId, apiRef: GridApiCommon, setInitial?: boolean, fieldKey?: keyof E) => void,
    assignationField: string = "driverId"
) => {
    if (!isEditable || !hasFocus || !selectionModel) return;

    if (cellMode === "view" && field === assignationField && event.key === "Backspace") {
        Object.keys(selectionModel).forEach((id) => {
            const row = api.getRow(id);

            const newRow = { ...row, [field]: null };
            processRowUpdate(newRow, row); // Needed to save values in unsavedChanges
            api.updateRows([{ id, ...newRow }]); // Needed for pasting
        });

        event.stopPropagation();
    } else if (event.key === "z" && (event.ctrlKey || event.metaKey)) {
        Object.keys(selectionModel).forEach((id) => {
            handleRowModesModelCancel(id, api, true, field as keyof E);
        });
        event.stopPropagation();
    }
};
